import cx from 'classnames';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import Divider from 'components/divider';
import { Heading } from 'components/typography';
import fuzzy from 'fuzzy';
import FilterableListBlock from './filterable-list-block';

import SearchIcon from 'icons/SearchIcon';

const HASH = '#';
const MINIMUM_SEARCH_TERM_LENGTH = 2;

export type FilterableListItem = {
  value: unknown;
  label: string;
};

type FilterableListProps = {
  title: string;
  searchInputPlaceholder: string;
  list: FilterableListItem[];
  linkTemplate?: (slug: string) => string;
  onItemClick?: (objectSelected: unknown, e?: React.MouseEvent) => void;
  selectedTopics?: string[];
  isInModal?: boolean;
};

type RepackedListItem = { letter: string; items: FilterableListItem[] };
type RepackedList = RepackedListItem[];

export default function FilterableList({
  title,
  searchInputPlaceholder,
  list,
  linkTemplate,
  onItemClick,
  selectedTopics,
  isInModal
}: FilterableListProps): React.ReactNode {
  const intl = useIntl();
  const noResultMessage = intl.formatMessage({
    defaultMessage: 'No items found'
  });
  const defaultPlaceholderText = intl.formatMessage({
    defaultMessage: 'Search'
  });
  const placeHolderText = searchInputPlaceholder || defaultPlaceholderText;

  const [inputValue, setInputValue] = useState('');
  const lowercaseFilter = inputValue.toLowerCase().trim();

  // filter list by search term
  let filteredList: Array<FilterableListItem>;

  // we don't filter the list before 2 characters typed (to many results)
  if (lowercaseFilter.length < MINIMUM_SEARCH_TERM_LENGTH) {
    filteredList = list;
  } else {
    const fuzzyListOptions = {
      extract: (e: FilterableListItem) => e.label
    };

    // if search value has exactly 2 characters we accept search results with (match) score over 3,
    // and if it has more than 2 characters we accept results with scores over 5
    const threshold =
      lowercaseFilter.length === MINIMUM_SEARCH_TERM_LENGTH ? 3 : 5;

    const fuzzyResult = fuzzy
      .filter(lowercaseFilter, list, fuzzyListOptions)
      .filter(result => result.score >= threshold);

    filteredList = fuzzyResult.map(e => {
      return e.original;
    });
  }

  // sort list alphabetically by name
  filteredList.sort((a: FilterableListItem, b: FilterableListItem) =>
    a.label.toUpperCase() > b.label.toUpperCase() ? 1 : -1
  );

  // group list items by starting letter
  const repacked = filteredList.reduce(
    (acc, item) => {
      const firstLetter = item.label[0].toUpperCase();
      const isLetter = firstLetter.toLowerCase() !== firstLetter.toUpperCase();
      // all items that don't start with letter (like 3d printing)
      // are grouped under "#"
      if (
        acc.list[acc.list.length - 1]?.letter.toUpperCase() !==
        (isLetter ? firstLetter : HASH)
      )
        acc.list.push({ letter: isLetter ? firstLetter : HASH, items: [] });
      acc.list[acc.list.length - 1].items.push(item);

      return acc;
    },
    { list: [] as RepackedList }
  );

  // move "#" group to end of the list
  if (repacked.list[0]?.letter === HASH) {
    const x = repacked.list.shift() as RepackedListItem;
    repacked.list.push(x);
  }

  const getSelectedIndexes = items => {
    const selectedIndexes: number[] = [];

    const selectedTopicsLowercase =
      selectedTopics?.map(topic => topic.toLowerCase()) ?? '';

    for (let i = 0; i < selectedTopicsLowercase.length; i += 1) {
      items.map((listItem, index) => {
        if (listItem.label === selectedTopicsLowercase[i]) {
          selectedIndexes.push(index);
        }

        return selectedIndexes;
      });
    }

    return selectedIndexes;
  };

  const headerClasses = cx('pt-5 md:pt-0', {
    'sticky top-0 z-10 -mt-18 rounded-t-md bg-white pt-6 md:static md:mt-0':
      isInModal
  });

  return (
    <div className="flex size-full flex-col pt-1 md:pt-16">
      <div className={headerClasses}>
        <div className="flex flex-col items-start rounded-t-md px-5 pb-8 md:flex-row md:items-center md:px-12 md:pb-10 xl:pb-8">
          <Heading
            size="l"
            tagName="h3"
            className="shrink-0 pb-10 md:w-1/5 md:pb-0"
          >
            {title}
          </Heading>
          <div className="flex w-full flex-row items-center">
            <SearchIcon className="mr-4 h-6 w-6" />
            <label htmlFor="filter-input" className="sr-only">
              {placeHolderText}
            </label>
            <input
              id="filter-input"
              value={inputValue}
              placeholder={placeHolderText}
              aria-label={placeHolderText}
              onChange={e => setInputValue(e.target.value)}
              className="w-full rounded-none border-b-thin border-transparent p-1 text-base outline-none transition-colors focus:border-black"
              autoComplete="off"
            />
          </div>
        </div>
        <Divider className="mx-5 pb-px lg:mx-12" />
      </div>
      {/* Added min height to keep "Not items found" message above the keyboard on iOS */}
      <div className="no-scrollbar h-auto min-h-60vh overflow-auto px-5 pb-4 md:px-12 lg:min-h-0">
        {repacked.list.length ? (
          repacked.list.map((listItem, index) => {
            return (
              <FilterableListBlock
                key={listItem.letter}
                title={listItem.letter}
                list={listItem.items}
                isLast={index === repacked.list.length - 1}
                linkTemplate={linkTemplate}
                onItemClick={onItemClick}
                selectedIndexes={getSelectedIndexes(listItem.items)}
              />
            );
          })
        ) : (
          <Heading size="m" tagName="p" className="pt-6">
            {noResultMessage}
          </Heading>
        )}
      </div>
    </div>
  );
}
